//@ts-strict-ignore
import React from 'react';
import Workspace from '../../components/Workspace/Workspace';
import { NominalTransaction } from '../../graphql/entities';
import getDocumentModalProps from '../../utils/getDocumentModalProps';
import Formatters from '../../components/grid-addons/formatters';
import fieldMetaData from '../../graphql/schema/fieldMetaData';
import { ALLOW_VIEW } from '../../utils/OperationPermissions';
import getAuditModalProps from '../../utils/getAuditModalProps';
import { useParams } from 'react-router';
import { TRANSACTION_DETAILS_SUPPORTED_DOCUMENT_TYPES } from '../../utils/TransactionDetailsDocTypes';
import { useWorkspace } from '../../graphql/hooks/useWorkspace';
import { Direction, FilterOperation, IFilterInput, INominalTransaction } from '../../graphql/schema/p4types';
import { IWorkspaceColumnOverride } from '../../@types/workspaceTypes';
import { Paths } from '../../@types/keyOfNested';
import { ModalFormatter } from '../../components/grid-addons/formatters/ModalFormatter';
import { FinancialTransactionModalFormatter } from '../../components/modals/FinancialTransactionModal';

const documentTypes = fieldMetaData.DocumentType;

const LazyFinancialAuditModal = React.lazy(() => import('../../components/modals/FinancialAuditModal'));
const LazyNominalForm = React.lazy(() => import('./NominalForm'));

const NOMINAL_WORKSPACE_FIELD_NAMES: Array<Paths<INominalTransaction>> = [
  'transactionNumber',
  'documentDate',
  'documentType',
  'documentNumber',
  'documentReference',
  'nominal.accountNo',
  'nominal.name',
  'financialPeriod.periodNumber',
  'financialPeriod.endDate',
  'baseAmount',
];

const NOMINAL_WORKSPACE_COLUMN_OVERRIDES: IWorkspaceColumnOverride = {
  transactionNumber: {
    name: 'Transaction No',
    formatter: ModalFormatter,
    options: {
      modalContent: LazyFinancialAuditModal,
      modalProps: getAuditModalProps,
      entityId: 'sourceId',
      isModal: (rowData) => {
        return TRANSACTION_DETAILS_SUPPORTED_DOCUMENT_TYPES.includes(rowData.documentType);
      },
    },
    width: 120,
  },
  'nominal.accountNo': {
    name: 'Account',
    formatter: ModalFormatter,
    width: 125,
    options: {
      modalContent: LazyNominalForm,
      entityId: 'nominal.id',
      modalProps: { forceReadOnly: true },
    },
  },
  'nominal.name': {
    name: 'Account Name',
    key: 'nominal.name',
  },
  'financialPeriod.name': {
    name: 'Financial Period',
    key: 'financialPeriod.name',
  },
  'financialPeriod.periodNumber': {
    name: 'Period No.',
    key: 'financialPeriod.periodNumber',
    formatter: Formatters.NumericFormatter,
    options: {
      format: {
        decimals: 0,
      },
    },
    width: 100,
  },
  'financialPeriod.endDate': {
    name: 'Period Ending',
    key: 'financialPeriod.endDate',
    width: 120,
    formatter: Formatters.DateFormatter,
  },
  documentType: {
    name: 'Source Type',
    key: 'documentType',
    width: 140,
    options: documentTypes,
    formatter: Formatters.EnumFormatter,
  },
  documentDate: {
    name: 'Date',
    key: 'documentDate',
    formatter: Formatters.DateFormatter,
    width: 110,
  },
  postingDate: {
    name: 'Posting Date',
    key: 'postingDate',
    formatter: Formatters.DateFormatter,
    width: 140,
  },
  baseAmount: {
    formatter: Formatters.NumericFormatter,
    width: 120,
    options: {
      format: { decimals: 2 },
    },
  },
  baseCreditAmount: {
    name: 'Credit Amount',
    formatter: Formatters.NumericFormatter,
    width: 120,
    options: {
      format: { decimals: 2 },
    },
    sortable: false,
  },
  baseDebitAmount: {
    name: 'Debit Amount',
    formatter: Formatters.NumericFormatter,
    width: 120,
    options: {
      format: { decimals: 2 },
    },
    sortable: false,
  },
  creditAmount: {
    name: 'Foreign Credit Amount',
    formatter: Formatters.NumericFormatter,
    width: 160,
    options: {
      format: { decimals: 2 },
    },
    sortable: false,
  },
  debitAmount: {
    name: 'Foreign Debit Amount',
    formatter: Formatters.NumericFormatter,
    width: 160,
    options: {
      format: { decimals: 2 },
    },
    sortable: false,
  },
  documentNumber: {
    name: 'Source No',
    formatter: FinancialTransactionModalFormatter,
    width: 125,
    options: {
      entityId: 'sourceId',
      modalProps: getDocumentModalProps,
    },
  },
};

const NominalTransactionWorkspace = (props) => {
  const params = useParams();
  const predefinedFilters: Array<IFilterInput> = params?.nominalAccountId
    ? [
        {
          filterOperation: FilterOperation.Equals,
          filterParameters: {
            field: 'nominal.id',
            value: params?.nominalAccountId,
          },
        },
      ]
    : [];
  const workspaceProps = useWorkspace<INominalTransaction>(props, NominalTransaction, {
    filters: predefinedFilters,
    columns: props.columns ? props.columns : NOMINAL_WORKSPACE_FIELD_NAMES,
    columnOverrides: props.columnOverrides ? props.columnOverrides : NOMINAL_WORKSPACE_COLUMN_OVERRIDES,
    initialSortColumn: props.initialSortColumn ? props.initialSortColumn : 'transactionNumber',
    initialSortDirection: props.initialSortDirection ? props.initialSortDirection : Direction.Desc,
  });

  return (
    <Workspace
      entityType={NominalTransaction.type}
      title={NominalTransaction.title}
      permittedOperations={ALLOW_VIEW}
      defaultQuery={NominalTransaction.workspace}
      {...workspaceProps}
      {...props}
      entity={NominalTransaction}
    />
  );
};

export default NominalTransactionWorkspace;
